import { HeroId } from './heroes';
import { InfinityStoneId } from './stones';

export const HERO_MAX_HP = 12;
export const ENEMY_MAX_HP_DEFAULT = 10;
export const ENEMY_ATTACK_INTERVAL_MS_DEFAULT = 10000;
export const ENEMY_ATTACK_DAMAGE_DEFAULT = 1;
export const POWER_MAX_CHARGES = 7;

export type BattlePhase = 'playing' | 'won' | 'lost';

export type BattleState = {
  heroHp: number;
  heroMaxHp: number;
  enemyHp: number;
  enemyMaxHp: number;
  enemyAttackTimerMs: number;
  enemyAttackIntervalMs: number;
  enemyAttackDamage: number;
  enemyAttackFreezeMs: number;
  skipNextAttacks: number;
  confusedAttacks: number;
  firstAttackFreeRemaining: boolean;
  usedStones: InfinityStoneId[];
  phase: BattlePhase;
  powerMeters: Partial<Record<HeroId, number>>;
};

export type BattleInit = {
  enemyMaxHp?: number;
  enemyAttackIntervalMs?: number;
  enemyAttackDamage?: number;
  heroMaxHp?: number;
  team?: HeroId[];
  firstAttackFree?: boolean;
};

export function initialBattle(opts: BattleInit = {}): BattleState {
  const heroMax = opts.heroMaxHp ?? HERO_MAX_HP;
  const enemyMax = opts.enemyMaxHp ?? ENEMY_MAX_HP_DEFAULT;
  const powerMeters: Partial<Record<HeroId, number>> = {};
  for (const id of opts.team ?? []) powerMeters[id] = 0;
  return {
    heroHp: heroMax,
    heroMaxHp: heroMax,
    enemyHp: enemyMax,
    enemyMaxHp: enemyMax,
    enemyAttackTimerMs: 0,
    enemyAttackIntervalMs: opts.enemyAttackIntervalMs ?? ENEMY_ATTACK_INTERVAL_MS_DEFAULT,
    enemyAttackDamage: opts.enemyAttackDamage ?? ENEMY_ATTACK_DAMAGE_DEFAULT,
    enemyAttackFreezeMs: 0,
    skipNextAttacks: 0,
    confusedAttacks: 0,
    firstAttackFreeRemaining: opts.firstAttackFree ?? false,
    usedStones: [],
    phase: 'playing',
    powerMeters,
  };
}

export function consumeConfusedAttack(state: BattleState): BattleState {
  if (state.confusedAttacks <= 0) return state;
  return { ...state, confusedAttacks: state.confusedAttacks - 1 };
}

export function consumeFirstAttackFree(state: BattleState): BattleState {
  if (!state.firstAttackFreeRemaining) return state;
  return { ...state, firstAttackFreeRemaining: false };
}

export function markStoneUsed(state: BattleState, id: InfinityStoneId): BattleState {
  if (state.usedStones.includes(id)) return state;
  return { ...state, usedStones: [...state.usedStones, id] };
}

export function freezeEnemyAttack(state: BattleState, ms: number): BattleState {
  if (ms <= 0) return state;
  return { ...state, enemyAttackFreezeMs: Math.max(state.enemyAttackFreezeMs, ms) };
}

export function queueAttackSkip(state: BattleState, count: number = 1): BattleState {
  if (count <= 0) return state;
  return { ...state, skipNextAttacks: state.skipNextAttacks + count };
}

export function consumeAttackSkip(state: BattleState): BattleState {
  if (state.skipNextAttacks <= 0) return state;
  return { ...state, skipNextAttacks: state.skipNextAttacks - 1 };
}

export function addPowerCharge(state: BattleState, heroId: HeroId, n: number): BattleState {
  if (n <= 0 || !(heroId in state.powerMeters)) return state;
  const current = state.powerMeters[heroId] ?? 0;
  const next = Math.min(POWER_MAX_CHARGES, current + n);
  if (next === current) return state;
  return { ...state, powerMeters: { ...state.powerMeters, [heroId]: next } };
}

export function consumePower(state: BattleState, heroId: HeroId): BattleState {
  if (!(heroId in state.powerMeters)) return state;
  return { ...state, powerMeters: { ...state.powerMeters, [heroId]: 0 } };
}

export function powerReady(state: BattleState, heroId: HeroId): boolean {
  return (state.powerMeters[heroId] ?? 0) >= POWER_MAX_CHARGES;
}

export function damageEnemy(state: BattleState, amount: number): BattleState {
  if (state.phase !== 'playing' || amount <= 0) return state;
  const enemyHp = Math.max(0, state.enemyHp - amount);
  return {
    ...state,
    enemyHp,
    enemyAttackTimerMs: 0,
    phase: enemyHp <= 0 ? 'won' : state.phase,
  };
}

export function damageHero(state: BattleState, amount: number): BattleState {
  if (state.phase !== 'playing' || amount <= 0) return state;
  const heroHp = Math.max(0, state.heroHp - amount);
  return {
    ...state,
    heroHp,
    phase: heroHp <= 0 ? 'lost' : state.phase,
  };
}

export function tickTimer(
  state: BattleState,
  dtMs: number,
): { state: BattleState; attacked: boolean } {
  if (state.phase !== 'playing') return { state, attacked: false };
  if (state.enemyAttackFreezeMs > 0) {
    const nextFreeze = Math.max(0, state.enemyAttackFreezeMs - dtMs);
    return { state: { ...state, enemyAttackFreezeMs: nextFreeze }, attacked: false };
  }
  let t = state.enemyAttackTimerMs + dtMs;
  let attacked = false;
  if (t >= state.enemyAttackIntervalMs) {
    t = 0;
    attacked = true;
  }
  return { state: { ...state, enemyAttackTimerMs: t }, attacked };
}

export function damageForMatch(size: number, comboStep: number): number {
  const over = Math.max(0, size - 2);
  const base = over === 0 ? 1 : (over * (over + 1)) / 2;
  return base * Math.max(1, comboStep);
}

export function computeStars(state: BattleState): 0 | 1 | 2 | 3 {
  if (state.phase !== 'won') return 0;
  const frac = state.heroHp / state.heroMaxHp;
  if (frac >= 0.8) return 3;
  if (frac >= 0.4) return 2;
  return 1;
}
